import { useRef, useState } from "react";
import { useI18n } from "../i18n";
import { FILE_ACCEPT, fileLabel, openFile, removeFile, uploadFile, type FileArea } from "../data";

interface Props {
  companyId: string;
  area: FileArea;
  ownerId: string;
  recordId: string;
  path: string | null;
  canEdit: boolean;
  onChanged: () => void | Promise<void>;
}

/** Shows an attached file (opens it privately) and lets permitted people attach, replace or remove it. */
export function FileSlot({ companyId, area, ownerId, recordId, path, canEdit, onChanged }: Props) {
  const { t } = useI18n();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try { await fn(); await onChanged(); } catch (e) { setError(e instanceof Error ? e.message : t("errorGeneric")); } finally { setBusy(false); }
  }
  if (!path && !canEdit) return null;
  return (
    <span className="file-slot">
      {path && <button type="button" className="link file-link" title={fileLabel(path)} onClick={() => run(() => openFile(path))}>📎 {fileLabel(path)}</button>}
      {canEdit && (
        <>
          <input ref={input} type="file" accept={FILE_ACCEPT} hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void run(() => uploadFile(companyId, area, ownerId, recordId, f)); }} />
          <button type="button" className="link" disabled={busy} onClick={() => input.current?.click()}>{busy ? t("fileUploading") : path ? t("fileReplace") : t("fileAttach")}</button>
          {path && !busy && <button type="button" className="link danger" onClick={() => { if (window.confirm(t("fileRemoveConfirm"))) void run(() => removeFile(area, recordId)); }}>{t("remove")}</button>}
        </>
      )}
      {error && <span className="field-error" role="alert">{error}</span>}
    </span>
  );
}

/** A file picker for a form that creates the record first (the file is uploaded after saving). */
export function FilePick({ file, onPick }: { file: File | null; onPick: (f: File | null) => void }) {
  const { t } = useI18n();
  return (
    <label className="field"><span>{t("fileLabel")}</span>
      <input type="file" accept={FILE_ACCEPT} onChange={(e) => onPick(e.target.files?.[0] ?? null)} />
      {file && <span className="muted small-note">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</span>}
    </label>
  );
}
